import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getBrowse } from "@/lib/browse";
import { imageUrl } from "@/lib/lamka";

// Prev/next across the list the shopper was browsing: swipe (touch), arrow keys, or the buttons.
export const useProductSwipe = (id) => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [dir, setDir] = useState(null);
  const touch = useRef(null);

  useEffect(() => { getBrowse().then(setList).catch(() => {}); }, []);

  const index = list.findIndex((x) => x.id === id);
  const prev = index > 0 ? list[index - 1] : null;
  const next = index >= 0 && index < list.length - 1 ? list[index + 1] : null;

  useEffect(() => {
    [prev, next].forEach((p) => { if (p?.img) new Image().src = imageUrl(p.img); });
  }, [prev, next]);

  const go = (target, d) => {
    if (!target) return;
    setDir(d);
    setTimeout(() => {
      navigate(`/product/${target.id}`, { replace: true });
      window.scrollTo({ top: 0 });
      setDir(null);
    }, 180);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || document.querySelector("[role=dialog],[role=alertdialog]")) return;
      if (e.key === "ArrowLeft") go(prev, "right");
      if (e.key === "ArrowRight") go(next, "left");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const handlers = {
    onTouchStart: (e) => {
      // Photo strip has its own horizontal swipe
      if (e.target.closest("[data-no-page-swipe]")) return (touch.current = null);
      const t = e.touches[0];
      touch.current = { x: t.clientX, y: t.clientY };
    },
    onTouchEnd: (e) => {
      if (!touch.current) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touch.current.x;
      const dy = t.clientY - touch.current.y;
      touch.current = null;
      if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      if (dx < 0) go(next, "left");
      else go(prev, "right");
    },
  };

  const slideClass = dir === "left" ? "-translate-x-6 opacity-0" : dir === "right" ? "translate-x-6 opacity-0" : "";
  return { prev, next, index, total: list.length, go, handlers, slideClass };
};

export const ProductPager = ({ prev, next, index, total, go }) => {
  if (total < 2 || index < 0) return null;
  const btn = "flex items-center gap-1 h-10 px-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] hover:text-[#0F0F0F] disabled:opacity-30 disabled:pointer-events-none";
  return (
    <div data-testid="product-pager" className="flex items-center justify-between mb-6 -mx-3">
      <button data-testid="product-prev-button" disabled={!prev} onClick={() => go(prev, "right")} className={btn}><ChevronLeft className="h-4 w-4" /> Previous</button>
      <span data-testid="product-pager-count" className="font-mono text-[10px] text-[#71717A]">{index + 1} / {total}</span>
      <button data-testid="product-next-button" disabled={!next} onClick={() => go(next, "left")} className={btn}>Next <ChevronRight className="h-4 w-4" /></button>
    </div>
  );
};
